import type { DecorationAttrs } from 'prosemirror-view';

/**
 * Awareness `user` payload that accompanies a remote collaborative cursor.
 * `SupabaseFileYjsProvider` sets `name` to the peer's email and `color` to a
 * 6-digit hex picked from the collaborator palette; `y-prosemirror` fills in
 * both fields with defaults before invoking the builders.
 */
export type RemoteCollabUser = {
  name: string;
  color: string;
  id?: string;
  avatarUrl?: string | null;
};

/** Same fallback `y-prosemirror` uses when a peer sends no usable color. */
const FALLBACK_COLOR = '#ffa500';

const HEX_COLOR = /^#[0-9a-fA-F]{6}$/;

function channelLuminance(value8Bit: number): number {
  const channel = value8Bit / 255;
  return channel <= 0.04045
    ? channel / 12.92
    : ((channel + 0.055) / 1.055) ** 2.4;
}

function relativeLuminance(hex: string): number {
  const value = hex.slice(1);
  const red = Number.parseInt(value.slice(0, 2), 16);
  const green = Number.parseInt(value.slice(2, 4), 16);
  const blue = Number.parseInt(value.slice(4, 6), 16);
  return (
    0.2126 * channelLuminance(red) +
    0.7152 * channelLuminance(green) +
    0.0722 * channelLuminance(blue)
  );
}

function contrastRatio(a: number, b: number): number {
  const lighter = Math.max(a, b);
  const darker = Math.min(a, b);
  return (lighter + 0.05) / (darker + 0.05);
}

/**
 * Picks whichever of white or black has the higher WCAG contrast ratio
 * against `hex`, so the floating peer label stays legible on every color in
 * the collaborator palette (all seven clear the 4.5:1 AA threshold for small
 * text against their pick). Unrecognized formats fall back to white, which
 * pairs with the fallback orange background.
 */
export function readableTextColor(hex: string): string {
  if (!HEX_COLOR.test(hex)) {
    return '#ffffff';
  }

  const luminance = relativeLuminance(hex);
  return contrastRatio(luminance, 1) >= contrastRatio(luminance, 0)
    ? '#ffffff'
    : '#000000';
}

function usableColor(color: string): string {
  return HEX_COLOR.test(color) ? color : FALLBACK_COLOR;
}

/**
 * Builds the widget decoration `y-prosemirror` inserts at a remote peer's
 * caret position.
 *
 * The returned wrapper is deliberately a zero-size inline box: the visible
 * caret line and the floating name label are absolutely positioned overlays
 * defined in `collab-cursor.css`, so rendering a peer's cursor can never
 * change line boxes, rewrap text, or intercept clicks in the document.
 * `aria-hidden` keeps the mid-text widget out of the accessibility tree —
 * collaborator presence is announced by the toolbar avatars instead.
 */
export function remoteCursorBuilder(user: RemoteCollabUser): HTMLElement {
  const color = usableColor(user.color);

  const wrapper = document.createElement('span');
  wrapper.className = 'B-remote-caret';
  wrapper.setAttribute('aria-hidden', 'true');

  const line = document.createElement('span');
  line.className = 'B-remote-caret__line';
  line.style.backgroundColor = color;

  const label = document.createElement('span');
  label.className = 'B-remote-caret__label';
  label.style.backgroundColor = color;
  label.style.color = readableTextColor(color);
  label.textContent = user.name;

  wrapper.append(line, label);
  return wrapper;
}

/**
 * Styles the inline decoration covering a remote peer's selection. It is an
 * in-flow background only — no layout boxes — so it cannot move text either.
 * The alpha keeps the passage readable underneath the highlight.
 */
export function remoteSelectionBuilder(
  user: RemoteCollabUser,
): DecorationAttrs {
  return {
    class: 'B-remote-selection',
    style: `background-color: ${usableColor(user.color)}59`,
  };
}
